import { useQuery } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch, apiPost } from "~/shared/api/client";
import { createMutationHook } from "~/shared/api/query-factories";
import { toSnakeCase } from "~/shared/lib/backend-mapper";
import {
    projectsKeys,
    type CreateProjectPayload,
    type ListProjectsParams,
    type Project,
    type ProjectListResponse,
    type UpdateProjectPayload,
} from "./api-types";

// ---------------------------------------------------------------------------
// Fetch functions — core project CRUD
// ---------------------------------------------------------------------------

function buildListQuery(params: ListProjectsParams): string {
    const usp = new URLSearchParams();
    if (params.tab) usp.set("tab", params.tab);
    if (params.workspaceId) usp.set("workspace_id", params.workspaceId);
    if (params.q) usp.set("q", params.q);
    if (params.sortBy) usp.set("sort_by", toSnakeCase(params.sortBy));
    if (params.sortDir) usp.set("sort_dir", params.sortDir);
    if (params.page) usp.set("page", String(params.page));
    if (params.pageSize) usp.set("page_size", String(params.pageSize));
    for (const v of params.status ?? []) usp.append("status", v);
    for (const v of params.type ?? []) usp.append("type", v);
    const qs = usp.toString();
    return qs ? `?${qs}` : "";
}

export async function fetchProjects(params: ListProjectsParams): Promise<ProjectListResponse> {
    return apiGet<ProjectListResponse>(`/v2/projects${buildListQuery(params)}`);
}

export async function fetchProject(projectId: string): Promise<Project> {
    return apiGet<Project>(`/v2/projects/${projectId}`);
}

export async function fetchCanAdminProject(projectId: string): Promise<boolean> {
    const res = await apiGet<{ canAdmin: boolean }>(`/v2/projects/${projectId}/can-admin`);
    return res.canAdmin;
}

export async function createProject(payload: CreateProjectPayload): Promise<Project> {
    const { workspaceId, ...body } = payload;
    return apiPost<Project>(`/v2/projects?workspace_id=${encodeURIComponent(workspaceId)}`, body);
}

export async function updateProject(projectId: string, patch: UpdateProjectPayload): Promise<Project> {
    return apiPatch<Project>(`/v2/projects/${projectId}`, patch);
}

export async function archiveProject(projectId: string): Promise<Project> {
    return apiPost<Project>(`/v2/projects/${projectId}/archive`, {});
}

export async function unarchiveProject(projectId: string): Promise<Project> {
    return apiPost<Project>(`/v2/projects/${projectId}/unarchive`, {});
}

export async function deleteProject(projectId: string): Promise<void> {
    await apiDelete<{ message: string }>(`/v2/projects/${projectId}`);
}

// ---------------------------------------------------------------------------
// Query hooks
// ---------------------------------------------------------------------------

export function useProjects(params: ListProjectsParams, options?: { enabled?: boolean }) {
    return useQuery<ProjectListResponse, Error>({
        queryKey: projectsKeys.list(params),
        queryFn: () => fetchProjects(params),
        enabled: options?.enabled ?? true,
    });
}

export function useProjectById(projectId: string | undefined) {
    return useQuery<Project, Error>({
        queryKey: projectId ? projectsKeys.byId(projectId) : ["projects", "detail", "skip"],
        queryFn: () => fetchProject(projectId!),
        enabled: Boolean(projectId),
    });
}

export function useCanAdminProject(projectId: string | undefined) {
    return useQuery<boolean, Error>({
        queryKey: projectId ? projectsKeys.canAdmin(projectId) : ["projects", "can-admin", "skip"],
        queryFn: () => fetchCanAdminProject(projectId!),
        enabled: Boolean(projectId),
    });
}

// ---------------------------------------------------------------------------
// Mutation hooks
// ---------------------------------------------------------------------------

export const useCreateProject = createMutationHook<Project, CreateProjectPayload>({
    mutationFn: createProject,
    invalidate: [projectsKeys.all],
});

export const useUpdateProject = createMutationHook<
    Project,
    { projectId: string; patch: UpdateProjectPayload }
>({
    mutationFn: ({ projectId, patch }) => updateProject(projectId, patch),
    onSuccess: (project, _vars, qc) => qc.setQueryData(projectsKeys.byId(project.id), project),
    invalidate: [projectsKeys.all],
});

export const useArchiveProject = createMutationHook<Project, string>({
    mutationFn: (projectId) => archiveProject(projectId),
    invalidate: [projectsKeys.all],
});

export const useUnarchiveProject = createMutationHook<Project, string>({
    mutationFn: (projectId) => unarchiveProject(projectId),
    invalidate: [projectsKeys.all],
});

export const useDeleteProject = createMutationHook<void, string>({
    mutationFn: (projectId) => deleteProject(projectId),
    invalidate: [projectsKeys.all],
});
